<script lang="ts" module>
  export interface PopupSection {
    heading: string
    paras: string[]
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte'

  import type { AlertTone } from '../forecast/tone'
  import { layout } from '../state/layout.svelte'
  import Icon from './Icon.svelte'

  /**
   * The frame an alert and a mesoscale discussion are read in, as round 2
   * draws it (Stage 10.5, 2d): modal, over a scrim; centred on the desktop and
   * a sheet from under the status bar on a phone. A tag and who issued it, the
   * title, a row of facts, the areas, a notice when the product has lapsed,
   * then the text itself, which is the only part that scrolls.
   *
   * Every string arrives from a third party and is interpolated as text. There
   * is no `{@html}` here, and `panels.test.ts` asserts that across the tree.
   */

  interface Props {
    /** The dialog's accessible name. */
    label: string
    /** The header close button's name; the footer's is "Close". */
    closeLabel: string
    tag: string
    /** `ink` or `outline`, or an alert's tier of the condition palette. */
    tone: AlertTone | 'ink' | 'outline'
    issuer?: string
    title: string
    meta?: { label: string; value: string; class?: string }[]
    areas?: string[]
    /** Why this is no longer current, drawn as a band above the text. */
    notice?: string
    headline?: string
    sections?: PopupSection[]
    source?: string
    onClose: () => void
    /** Loading, errors, and anything a product has that the frame does not. */
    children?: Snippet
  }

  let {
    label,
    closeLabel,
    tag,
    tone,
    issuer = '',
    title,
    meta = [],
    areas = [],
    notice = '',
    headline = '',
    sections = [],
    source = '',
    onClose,
    children,
  }: Props = $props()

  /** A county list can run to forty names; the first few say where. */
  const AREAS_SHOWN = 8
  let allAreas = $state(false)
  let shownAreas = $derived(allAreas ? areas : areas.slice(0, AREAS_SHOWN))

  let closeButton: HTMLButtonElement | undefined = $state()

  // Focus moves into the dialog when it opens and goes back where it came
  // from when it closes, which is what a modal owes a keyboard.
  $effect(() => {
    const before = document.activeElement as HTMLElement | null
    closeButton?.focus()
    return () => before?.focus?.()
  })

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose()
  }
</script>

<svelte:window onkeydown={onKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
<div class="scrim" onclick={onClose}></div>
<div class="popup" class:sheet={!layout.isDesktop} role="dialog" aria-modal="true" aria-label={label}>
  <header>
    <div class="heading">
      <div class="tags">
        <span class="tag {tone}" data-tone={tone === 'ink' || tone === 'outline' ? undefined : tone}>{tag}</span>
        {#if issuer}<span class="issuer">{issuer}</span>{/if}
      </div>
      <h2 class="event">{title}</h2>
    </div>
    <button type="button" class="close touch-target" aria-label={closeLabel} onclick={onClose} bind:this={closeButton}>
      <Icon name="x" size={layout.isDesktop ? 20 : 22} strokeWidth={2.2} />
    </button>
  </header>

  {#if meta.length > 0}
    <dl class="meta">
      {#each meta as m (m.label)}
        <div class="fact {m.class ?? ''}">
          <dt>{m.label}</dt>
          <dd>{m.value}</dd>
        </div>
      {/each}
    </dl>
  {/if}

  {#if areas.length > 0}
    <div class="areas">
      <span class="label">Areas</span>
      {#each shownAreas as area (area)}<span class="area">{area}</span>{/each}
      {#if areas.length > AREAS_SHOWN}
        <button type="button" class="more" onclick={() => (allAreas = !allAreas)}>
          {allAreas ? 'Fewer' : `+${areas.length - AREAS_SHOWN} more`}
        </button>
      {/if}
    </div>
  {/if}

  {#if notice}
    <p class="notice gone"><Icon name="clock" size={18} /><span>{notice}</span></p>
  {/if}

  <div class="body">
    {#if headline}<p class="headline">{headline}</p>{/if}
    {#each sections as section (section.heading)}
      <section>
        <h3 class="eyebrow">{section.heading}</h3>
        {#each section.paras as para, i (i)}<p class="text">{para}</p>{/each}
      </section>
    {/each}
    {#if children}{@render children()}{/if}
  </div>

  <!-- The phone's sheet has its close in the header and runs the text to the
       bottom edge, as the handoff draws it. -->
  {#if layout.isDesktop}
    <footer>
      <span class="source">{source}</span>
      <button type="button" class="done touch-target" onclick={onClose}>Close</button>
    </footer>
  {/if}
</div>

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: rgb(20 18 17 / 55%);
  }

  .popup {
    position: fixed;
    z-index: 51;
    left: 50%;
    top: 40px;
    transform: translateX(-50%);
    width: min(680px, calc(100vw - 32px));
    height: min(720px, calc(100dvh - 80px));
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    color: var(--color-text);
    border-top: 2px solid var(--color-text);
    box-shadow: var(--shadow-lg);
    --px: 28px;
  }

  .popup.sheet {
    left: 0;
    right: 0;
    top: max(var(--safe-top), 12px);
    bottom: 0;
    transform: none;
    width: auto;
    height: auto;
    box-shadow: none;
    --px: 20px;
  }

  header {
    flex: none;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 12px 16px var(--px);
  }

  .sheet header {
    gap: 8px;
    padding: 14px 6px 14px var(--px);
  }

  .heading {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .sheet .tags {
    gap: 8px;
  }

  .tag {
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .tag[data-tone] {
    background: var(--color-field);
    color: var(--color-on-field);
  }

  .tag.ink {
    background: var(--color-text);
    color: var(--color-bg);
  }

  .tag.outline {
    padding: 0 6px;
    border: 2px solid var(--color-text);
  }

  .issuer {
    font-size: 13px;
    color: var(--color-muted);
  }

  .sheet .issuer {
    font-size: 12px;
  }

  h2 {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }

  .sheet h2 {
    font-size: 26px;
    letter-spacing: 0;
  }

  .close {
    flex: none;
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .sheet .close {
    width: 44px;
    height: 44px;
  }

  .close:hover,
  .done:hover,
  .more:hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .meta {
    flex: none;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0;
    border-top: 2px solid var(--color-divider);
    border-bottom: 2px solid var(--color-divider);
  }

  .fact {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px 12px var(--px);
    border-right: 1px solid var(--color-divider);
  }

  .fact:last-child {
    border-right: 0;
  }

  .sheet .meta {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-bottom: 0;
  }

  .sheet .fact {
    padding: 8px 12px 10px var(--px);
    border-bottom: 1px solid var(--color-divider);
  }

  .sheet .fact:nth-child(odd) {
    border-right: 1px solid var(--color-divider);
  }

  .sheet .fact:nth-child(even) {
    border-right: 0;
  }

  dt,
  .label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-muted);
  }

  dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
  }

  .areas {
    flex: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    max-height: 30dvh;
    overflow-y: auto;
    padding: 10px var(--px);
    border-bottom: 2px solid var(--color-divider);
  }

  .area {
    padding: 3px 8px;
    background: var(--color-surface);
    font-size: 13px;
    font-weight: 600;
  }

  .more {
    padding: 3px 8px;
    border: 0;
    background: none;
    font-size: 13px;
    font-weight: 800;
    text-decoration: underline;
    cursor: pointer;
  }

  .notice {
    flex: none;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0;
    padding: 12px var(--px);
    background: var(--color-surface);
    border-bottom: 2px solid var(--color-divider);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
  }

  .notice :global(.icon) {
    margin-top: 1px;
  }

  .body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px var(--px) 20px;
  }

  .sheet .body {
    padding: 16px var(--px) calc(20px + var(--safe-bottom));
  }

  .headline {
    margin: 0;
    max-width: 60ch;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.45;
    text-wrap: pretty;
  }

  section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 14px;
    border-top: 1px solid var(--color-divider);
  }

  h3 {
    margin: 0;
  }

  .text {
    margin: 0;
    max-width: 64ch;
    font-size: 14px;
    line-height: 1.55;
    text-wrap: pretty;
    overflow-wrap: anywhere;
  }

  .sheet .text {
    font-size: 15px;
  }

  footer {
    flex: none;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px 12px var(--px);
    border-top: 2px solid var(--color-divider);
  }

  .source {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    color: var(--color-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .done {
    height: 40px;
    padding: 0 14px;
    border: 2px solid var(--color-text);
    background: none;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
  }
</style>
